import SubTitle from '@/pages/WgqAllMaps/components/SubTitle';
import { DoubleRightOutlined } from '@ant-design/icons';
import { Button, DatePicker, Empty, Tabs } from '@maxtropy/components';
import { Space } from 'antd';
import { memo, useContext, useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { EnergyFlowChartNodeItem, getEnergyFlowChart } from '@/api/plustekSankey';
import { ShareDataUnityEnergyCockpitMapContext } from '../../../..';
import { isNil } from 'lodash-es';
import FlowEcharts from './FlowEcharts';
import qs from 'qs';
import { getDefaultTime } from '@/pages/WgqAllMaps/utils';
import { IndexTimeActive } from '../SwitchDate';
import titlePng from '@/assets/images/title_icon.png';

import styles from './index.module.scss';
import FlowSwitchDate, { DateTypeByWater } from './FlowSwitchDate';
import { formatTime } from '@/pages/WgqAllMaps/components/UnityWaterMap/components/LeftBox/components/UseWaterEnergyFlow';

const RangePicker = DatePicker.RangePicker;
type RangeValue = [Dayjs | null, Dayjs | null] | null;

const filterSankeyData = (allNodes: EnergyFlowChartNodeItem[], fromLevel: number, toLevel: number) => {
  const nodes = [...allNodes].filter(i => i.depth + 1 >= fromLevel && i.depth + 1 <= toLevel);
  // 3. 重新映射 level，从 0 开始
  const minLevel = Math.min(...nodes.map(n => n.depth));
  return nodes.map(n => ({
    ...n,
    depth: n.depth - minLevel, // 重新映射
  }));
};

const EnergyConsumptionFlow = () => {
  const { configDetail, timeResolution, dateValue } = useContext(ShareDataUnityEnergyCockpitMapContext);
  const [onlyKey, setOnlyKey] = useState<string>();
  const [flowTimeResolution, setFlowTimeResolution] = useState<DateTypeByWater>(DateTypeByWater.DAY);
  const [dates, setDates] = useState<RangeValue>(null);
  const [value, setValue] = useState<RangeValue>([dayjs().startOf('month'), dayjs()]);
  const [rangeTime, setRangeTime] = useState<RangeValue>();

  const { energyFlowConfigs } = configDetail || {};

  useEffect(() => {
    if (!flowTimeResolution) return;
    if (flowTimeResolution === DateTypeByWater.DAY) {
      setRangeTime([value?.[0]?.startOf('D') ?? null, value?.[1]?.endOf('D') ?? null]);
    } else {
      setRangeTime([value?.[0]?.startOf('month') ?? null, value?.[1] ?? null]);
    }
  }, [value, flowTimeResolution]);

  // 用能趋势
  const { data: flowChartData, loading: flowChartLoading } = useRequest(
    async () => {
      const selectedMediumId = energyFlowConfigs?.find(i => i.energyGroup?.id + '-' + i.medium?.id === onlyKey)?.medium
        ?.id;
      if (!selectedMediumId) return;

      const t = formatTime(rangeTime as [dayjs.Dayjs, dayjs.Dayjs], flowTimeResolution);

      const res = await getEnergyFlowChart({
        ...t,
        energyUnitGroupId: onlyKey?.split('-')[0]!,
        energyMediumId: selectedMediumId,
      });
      let chartDataTemp = {
        nodes: (res.nodes ?? [])
          .map(item => ({
            ...item,
            name: item.key,
          }))
          .filter(c => !isNil(c.value) && c.value !== 0),
        links: res.links.filter(item => !isNil(item.value) && item.value !== 0),
      };
      const fromLevel = energyFlowConfigs?.find(
        item => item.energyGroup?.id + '-' + item.medium?.id === onlyKey
      )?.fromLevel;
      const toLevel = energyFlowConfigs?.find(
        item => item.energyGroup?.id + '-' + item.medium?.id === onlyKey
      )?.toLevel;
      if (fromLevel && toLevel) {
        return {
          nodes: filterSankeyData(chartDataTemp.nodes, fromLevel, toLevel),
          links: chartDataTemp.links,
        };
      }
    },
    {
      ready: !!onlyKey && !!rangeTime && !!flowTimeResolution,
      refreshDeps: [onlyKey, rangeTime, flowTimeResolution],
    }
  );

  // 默认选择一个用能分析组
  useEffect(() => {
    if (!configDetail) return;
    setOnlyKey(energyFlowConfigs?.[0]?.energyGroup?.id + '-' + energyFlowConfigs?.[0]?.medium?.id);
  }, [energyFlowConfigs]);

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };
  const onRadioChange = (value: DateTypeByWater) => {
    setFlowTimeResolution(value);
    if (value === DateTypeByWater.DAY) {
      setValue([dayjs().subtract(3, 'day'), dayjs().subtract(1, 'day')]);
    } else {
      setValue([dayjs().startOf('month'), dayjs()]);
    }
  };
  // 时间跨度24个月
  const customDisabledMonthDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    return current && current >= dayjs().endOf('day');
  };

  // 时间跨度为3个月
  const customDisabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'months') >= 3;
    const tooEarly = current && current > dayjs().endOf('month');
    return !!tooEarly || !!tooLate;
  };

  const getSankeyQueryTime = () => {
    const defaultTime = getDefaultTime(timeResolution!, dateValue);
    const isCurrentMonth = timeResolution === IndexTimeActive.THIS_MONTH && dayjs(dateValue).isSame(dayjs(), 'month');

    return {
      fromTime: dayjs(defaultTime.startTime).format('YYYY-MM-DD'),
      toTime: dayjs(isCurrentMonth ? dayjs() : defaultTime.endTime).format('YYYY-MM-DD'),
    };
  };

  return (
    <SubTitle
      title="用能流向"
      img={titlePng}
      style={{ paddingTop: 8, flex: '1 1 32%' }}
      headerStyle={{ paddingBottom: 4 }}
      extraContent={
        <Button
          type="link"
          onClick={() => {
            const { fromTime, toTime } = getSankeyQueryTime();

            window.open(
              `/energy/analysis/unitSankey?${qs.stringify(
                {
                  radioType: timeResolution == IndexTimeActive.THIS_MONTH ? 1 : 2,
                  fromTime,
                  toTime,
                  unitGroupId: onlyKey?.split('-')[0],
                },
                { indices: false }
              )} `,
              '_blank'
            );
          }}
        >
          详情
          <DoubleRightOutlined className="translationStyles" />
        </Button>
      }
    >
      <div className={styles.useEnergy_wrapper}>
        <div className={styles.header}>
          <Tabs
            className={styles.tabsStyle}
            activeKey={onlyKey}
            getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
            onChange={v => {
              setOnlyKey(v);
            }}
            items={(energyFlowConfigs ?? []).map(i => {
              return {
                label: i.showEnergyGroupName,
                key: i.energyGroup?.id + '-' + i.medium?.id,
              };
            })}
          />
          {/* <Space size={8}>
            <FlowSwitchDate
              defaultValue={flowTimeResolution}
              onChange={onRadioChange}
              options={[
                { label: '按日', value: DateTypeByWater.DAY },
                { label: '按月', value: DateTypeByWater.MONTH },
              ]}
            />
            <RangePicker
              value={dates || value}
              getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
              disabledDate={flowTimeResolution === DateTypeByWater.DAY ? customDisabledDate : customDisabledMonthDate}
              onCalendarChange={val => setDates(val)}
              onChange={val => {
                setValue(val);
              }}
              onOpenChange={onOpenChange}
              picker={flowTimeResolution === DateTypeByWater.MONTH ? 'month' : undefined}
              allowClear={false}
            />
          </Space> */}
        </div>
        <div className={styles.content}>
          {flowChartData ? (
            <FlowEcharts
              flowChartLoading={flowChartLoading}
              isMedium={
                energyFlowConfigs?.find(i => i.energyGroup?.id + '-' + i.medium?.id === onlyKey)?.medium?.id !== -1
              }
              data={flowChartData}
              physicalUnitName={
                energyFlowConfigs?.find(i => i.energyGroup?.id + '-' + i.medium?.id === onlyKey)?.physicalUnitName
              }
            />
          ) : (
            <Empty />
          )}
        </div>
      </div>
    </SubTitle>
  );
};

export default memo(EnergyConsumptionFlow);
